import * as React from 'react';
import { useQuery } from 'react-query';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  FormHelperText,
  Box,
  IconButton,
  InputAdornment,
  Popover,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FieldOptionReadDTO } from '../generated/api-client';
import { apiClient } from '../services/apiClient';

export interface FieldOptionDropdownProps {
  tableCode: string;
  value?: string | null;
  onChange?:
    | ((value: string | null) => void)
    | ((event: { target: { value: string | null; name?: string } }) => void);
  onBlur?: (() => void) | ((event: { target: { name?: string } }) => void);
  name?: string;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string | React.ReactNode;
  required?: boolean;
  fullWidth?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  isNullable?: boolean;
  getOptionLabel?: (option: FieldOptionReadDTO) => string;
  getOptionValue?: (option: FieldOptionReadDTO) => string;
}

export const FieldOptionDropdown = React.forwardRef<HTMLDivElement, FieldOptionDropdownProps>(
  (
    {
      tableCode,
      value,
      onChange,
      onBlur,
      name,
      label = 'Field Option',
      disabled = false,
      error = false,
      helperText,
      required = false,
      fullWidth = true,
      variant = 'outlined',
      isNullable = false,
      getOptionLabel = option => option.option_value || '',
      getOptionValue = option => option.option_value || '',
    },
    ref
  ) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [isFocused, setIsFocused] = React.useState(false);

    const {
      data: options = [],
      isLoading,
      error: queryError,
    } = useQuery<FieldOptionReadDTO[]>(
      ['fieldOptions', tableCode],
      async () => {
        if (!tableCode) {
          return [];
        }
        try {
          return await apiClient.search_by_table_code_field_option_search_by_table_code__table_code__get(
            tableCode
          );
        } catch (err: any) {
          // Se restituisce 404, restituiamo array vuoto invece di lanciare errore
          if (err?.response?.status === 404) {
            return [];
          }
          throw err;
        }
      },
      {
        enabled: !!tableCode,
        retry: false,
      }
    );

    const handleChange = (event: SelectChangeEvent<string>) => {
      const newValue = event.target.value;
      if (onChange) {
        // Controller di react-hook-form passa onChange che accetta direttamente il valore
        // Chiamiamo sempre con il valore diretto, che funziona sia con Controller che con register
        (onChange as any)(newValue === '' ? null : newValue);
      }
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onChange) {
        (onChange as any)(null);
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (onBlur) {
        // Prova a chiamare come funzione react-hook-form (con evento)
        try {
          (onBlur as (event: { target: { name?: string } }) => void)({ target: { name } });
        } catch {
          // Se fallisce, prova come funzione normale (senza parametri)
          (onBlur as () => void)();
        }
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    const handleCopyTableCode = async () => {
      try {
        await navigator.clipboard.writeText(tableCode);
        handlePopoverClose();
      } catch (err) {
        console.error('Failed to copy table code:', err);
      }
    };

    const open = Boolean(anchorEl);

    const getLabel = (option: FieldOptionReadDTO): string => {
      const label = getOptionLabel(option);
      return typeof label === 'string' ? label : String(label || '');
    };

    const selectValue = value !== undefined && value !== null ? String(value) : '';
    const labelId = `field-option-label-${tableCode}-${name || ''}`;
    const showClearButton = isNullable && selectValue !== '' && !disabled && !isLoading;

    const helperTextValue: string =
      typeof helperText === 'string'
        ? helperText
        : queryError
          ? 'Errore nel caricamento delle opzioni'
          : '';

    return (
      <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
        <FormControl
          fullWidth={fullWidth}
          error={error || !!queryError}
          disabled={disabled || isLoading}
          required={required}
        >
          <>
            {label && <InputLabel id={labelId}>{label}</InputLabel>}
            <Select
              ref={ref}
              name={name}
              labelId={label ? labelId : undefined}
              value={selectValue}
              onChange={handleChange}
              onBlur={handleBlur}
              label={label}
              disabled={disabled || isLoading}
              variant={variant}
              endAdornment={
                isLoading ? (
                  <CircularProgress size={20} />
                ) : showClearButton ? (
                  <InputAdornment position="end" sx={{ mr: 2 }}>
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      onMouseDown={e => e.preventDefault()}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }
            >
              {options.length === 0 && !isLoading && (
                <MenuItem disabled value="">
                  {tableCode ? 'Nessuna opzione disponibile' : 'Inserisci un Table Code'}
                </MenuItem>
              )}
              {options.map(option => {
                const optionValue = getOptionValue(option);
                const optionLabel = getLabel(option);
                return (
                  <MenuItem key={optionValue} value={optionValue}>
                    {optionLabel}
                  </MenuItem>
                );
              })}
            </Select>
            {(helperText || queryError) && helperTextValue && (
              <FormHelperText>{helperTextValue}</FormHelperText>
            )}
          </>
        </FormControl>
      </Box>
    );
  }
);

FieldOptionDropdown.displayName = 'FieldOptionDropdown';
